import React, { useMemo } from 'react';

import styled from 'styled-components';

import { Flex } from '@adsk/alloy-react-box';
import theme from '@adsk/alloy-react-theme';

import EntityPickerModalListContent from './EntityPickerModalListContent';
import EntityPickerModalNoData from './EntityPickerModalNoData';
import { CommonReferencePickerPlugin } from '../types/ReferencePickerPlugin';
import {
  PluginQuerySort,
  PluginQueryFilters,
  AddEntitiesToCacheProps,
  SetSelectionHandler,
  EntityWithId,
} from '../types/types';

interface EntityPickerModalContentProps<T> {
  loading?: boolean;
  error?: boolean | object;
  onTryAgain: () => void;
  onLoadMore: (() => Promise<void>) | null;
  selectedIds: string[];
  disabledIds?: string[];
  multiple?: boolean;
  entities: T[];
  onChange: (params: { entity: T; checked?: boolean }) => void;
  setSelection: SetSelectionHandler<T>;
  disabled?: boolean;
  filters: PluginQueryFilters;
  setFilters: (filters: PluginQueryFilters) => void;
  openFiltersPanel: () => void;
  plugin: CommonReferencePickerPlugin;
  addEntitiesToCache?: (option: AddEntitiesToCacheProps<T>) => void;
  canCreate: boolean;
  onCreateClick: () => void;
  dataViewProps?: Record<string, unknown>;
  onUpdate?: (entity: T) => void;
  sort?: PluginQuerySort;
  setSort?: (sortOptions: PluginQuerySort | undefined) => void;
  isPluginDisabledInEntitlements?: boolean;
}

const StyledFlex = styled(Flex)<{ disabled: boolean }>`
  pointer-events: ${(props) => (props.disabled ? 'none' : 'auto')};
  * {
    pointer-events: inherit;
  }
`;

const EntityPickerModalContent = <T extends EntityWithId>({
  onLoadMore,
  selectedIds,
  disabledIds = [],
  multiple,
  entities,
  addEntitiesToCache,
  loading,
  error,
  onTryAgain,
  onChange,
  setSelection,
  disabled,
  filters,
  setFilters,
  openFiltersPanel,
  plugin,
  canCreate,
  onCreateClick,
  dataViewProps,
  onUpdate,
  sort,
  setSort,
  isPluginDisabledInEntitlements,
}: EntityPickerModalContentProps<T>) => {
  const hasData = useMemo(() => {
    if (typeof dataViewProps?.dataFilter === 'function') {
      const filteredResults = dataViewProps.dataFilter(entities);
      return !!filteredResults.length;
    }
    return !!entities.length;
  }, [entities, dataViewProps]);
  const DataView = plugin.CustomDataView || EntityPickerModalListContent;

  return (
    <StyledFlex
      flexDirection="column"
      overflowY="auto"
      flex="1"
      borderRadius="2px"
      border={`1px solid ${theme.colors.charcoal200}`}
      {...(!hasData && {
        alignItems: 'center',
        justifyContent: 'center',
      })}
      {...(disabled && {
        opacity: 0.3,
      })}
      disabled={!!disabled}
    >
      {hasData ? (
        <DataView
          plugin={plugin}
          entities={entities}
          addEntitiesToCache={addEntitiesToCache}
          onLoadMore={onLoadMore}
          disabledIds={disabledIds}
          selectedIds={selectedIds}
          multiple={!!multiple}
          setSelection={setSelection}
          onChange={onChange}
          loading={!!loading}
          error={!!error}
          onTryAgain={onTryAgain}
          filters={filters}
          setFilters={setFilters}
          openFiltersPanel={openFiltersPanel}
          dataViewProps={dataViewProps}
          onUpdate={onUpdate}
          sort={sort}
          setSort={setSort}
          isPluginDisabledInEntitlements={isPluginDisabledInEntitlements}
        />
      ) : (
        <EntityPickerModalNoData
          plugin={plugin}
          loading={loading}
          error={error}
          onTryAgain={onTryAgain}
          filters={filters}
          setFilters={setFilters}
          canCreate={canCreate}
          onCreateClick={onCreateClick}
          dataViewProps={dataViewProps}
        />
      )}
    </StyledFlex>
  );
};

export default EntityPickerModalContent;
